import React from "react";
import { CurrentCostsRow } from "./CurrentCostsRow";

// Helper functions for formatting
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export function CurrentCostsTable({
  lineItems,
  currentProvider,
  isAdp,
  adpUnbundled,
  adminFeeType,
  setAdminFeeType,
  adminFeeValue,
  setAdminFeeValue,
  serviceFees,
  setServiceFees,
  currentTotals,
  currentAdminFee,
  currentTotal,
  totalServiceFees,
  // Line item editing
  editingSutaState,
  editingSutaValue,
  setEditingSutaValue,
  startEditingSuta,
  updateCurrentStateSutaRate,
  cancelEditingSuta,
  startEditItem,
  removeLineItem,
  // Helper functions
  calculate,
  sutaBases,
  WC_DESCRIPTIONS,
}) {
  const isAdminFeeDisabled = isAdp && !adpUnbundled;

  return (
    <div className="border-2 border-red-300 rounded-xl shadow-sm overflow-hidden bg-red-50/30" data-testid="current-costs-table">
      {/* Header */}
      <div className="px-5 py-3 border-b border-red-200 bg-red-50/60">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold font-['Outfit'] text-red-800">
            Current Costs
            {currentProvider && (
              <span className="text-red-400 font-normal ml-2">({currentProvider})</span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            <label className={`text-xs font-medium uppercase tracking-wider ${isAdminFeeDisabled ? "text-slate-300" : "text-slate-500"}`}>
              Admin Fee
            </label>
            <select 
              data-testid="admin-fee-type-select" 
              value={adminFeeType} 
              onChange={e => setAdminFeeType(e.target.value)}
              disabled={isAdminFeeDisabled}
              className={`h-8 rounded-lg border px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#3c68ff] ${isAdminFeeDisabled ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed" : "border-slate-200 bg-white"}`}
            >
              <option value="pepm">PEPM</option>
              <option value="percent">% of Payroll</option>
            </select>
            <input 
              data-testid="admin-fee-value-input" 
              type="number" 
              step="0.01" 
              value={isAdminFeeDisabled ? "" : adminFeeValue} 
              onChange={e => setAdminFeeValue(e.target.value)}
              disabled={isAdminFeeDisabled}
              placeholder={isAdminFeeDisabled ? "Bundled" : (adminFeeType === "pepm" ? "$/mo/EE" : "%")}
              className={`w-24 h-8 rounded-lg border px-2 text-xs text-right focus:outline-none focus:ring-2 focus:ring-[#3c68ff] ${isAdminFeeDisabled ? "border-slate-100 bg-slate-50 text-slate-300 placeholder:text-slate-300 cursor-not-allowed" : "border-slate-200 bg-white"}`} 
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-red-50/80">
              <th className="px-3 py-2 text-left text-xs font-semibold text-red-600 uppercase tracking-wider">State</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-red-600 uppercase tracking-wider">WC Code</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">EEs</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">Gross Wages</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">
                {isAdp && !adpUnbundled ? "ADP Svc Fee" : "WC Rate"}
              </th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">
                {isAdp && !adpUnbundled ? "Svc Fee Amt" : "WC Premium"}
              </th>
              {isAdp && adpUnbundled && (
                <>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">Svc Fee %</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">Svc Fee Amt</th>
                </>
              )}
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">SUTA Rate</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-red-600 uppercase tracking-wider">SUTA Tax</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-red-600 uppercase tracking-wider w-16">Actions</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map(item => (
              <CurrentCostsRow
                key={item.id}
                item={item}
                isAdp={isAdp}
                adpUnbundled={adpUnbundled}
                serviceFees={serviceFees}
                setServiceFees={setServiceFees}
                editingSutaState={editingSutaState}
                editingSutaValue={editingSutaValue}
                setEditingSutaValue={setEditingSutaValue}
                startEditingSuta={startEditingSuta}
                updateCurrentStateSutaRate={updateCurrentStateSutaRate}
                cancelEditingSuta={cancelEditingSuta}
                startEditItem={startEditItem}
                removeLineItem={removeLineItem}
                calculate={calculate}
                sutaBases={sutaBases}
                WC_DESCRIPTIONS={WC_DESCRIPTIONS}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-5 py-3 bg-red-50/60 border-t border-red-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="text-slate-500">
              Employees: <span className="font-semibold text-slate-700">{currentTotals.employees}</span>
            </span>
            <span className="text-slate-500">
              Gross Wages: <span className="font-semibold text-slate-700">{fmt(currentTotals.grossWages)}</span>
            </span>
            <span className="text-slate-500">
              WC Premium: <span className="font-semibold text-slate-700">{fmt(currentTotals.wcPremium)}</span>
            </span>
            <span className="text-slate-500">
              SUTA Tax: <span className="font-semibold text-slate-700">{fmt(currentTotals.sutaTax)}</span>
            </span>
            {isAdp && adpUnbundled && totalServiceFees > 0 && (
              <span className="text-slate-500">
                Service Fees: <span className="font-semibold text-red-600">{fmt(totalServiceFees)}</span>
              </span>
            )}
            <span className="text-slate-500">
              Admin Fee: <span className="font-semibold text-slate-700">{fmt(currentAdminFee)}</span>
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs text-red-500 uppercase tracking-wider mr-2">Grand Total</span>
            <span className="text-xl font-bold text-red-700">{fmt(currentTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
